import { useQuery } from '@tanstack/react-query';
import { Loader2, Star, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';

const RANGES: Array<[number, string]> = [
  [7, 'This week'],
  [30, '30 days'],
  [90, '90 days'],
];

/**
 * A technician's own figures, on their own screen.
 *
 * They were only visible to admins and dispatchers, which made them a thing
 * done *to* the field team rather than something they could act on. Somebody
 * who cannot see that four of their jobs were reopened cannot stop it
 * happening a fifth time.
 *
 * Written to be encouraging without being dishonest. The good numbers lead,
 * the ones that need attention are stated plainly and only appear when they are
 * not zero — a row of zeroes under a heading like "Missed" is a daily reminder
 * of nothing. There is deliberately **no overall score**: a single number
 * ranking people invites gaming and tells nobody what to do differently.
 */
export function MyPerformance() {
  const [days, setDays] = useState(30);

  const { data, isPending } = useQuery({
    queryKey: ['my-performance', days],
    queryFn: () => api.myPerformance(days),
  });

  if (isPending) {
    return (
      <div className="card flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  const p = data?.performance;

  // A technician account not yet linked to a technician record has nothing to
  // measure. Saying so beats a panel of zeroes that looks like bad work.
  if (!p) {
    return (
      <div className="card">
        <h3 className="font-heading text-lg">Your performance</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Nothing to show yet. Your account is not linked to a technician record — ask an admin to
          connect it, and your figures will appear here.
        </p>
      </div>
    );
  }

  const good = [
    { label: 'Jobs completed', value: p.completed, hint: 'Finished and signed off' },
    {
      /*
       * The target, next to the figure it is measured against. A number with no
       * target beside it is a number nobody knows what to do with.
       */
      label: 'Jobs a working day',
      value: p.perDay,
      hint: `Target is ${p.dailyTarget}. Counted per working day, so time off does not count against you.`,
    },
    { label: 'Fixed first time', value: p.fixed, hint: 'No repeat visit needed' },
    {
      label: 'Typical time on site',
      value: p.medianOnJobLabel ?? '—',
      hint: 'Middle of your visits, not the average',
    },
  ];

  /** Only shown when there is something there. Zeroes are not a lecture. */
  const attention = [
    {
      label: 'Visits over 2 hours',
      value: p.overran,
      hint:
        p.visits === 0
          ? 'No visits with an arrival time recorded yet'
          : `Of ${p.visits} visit${p.visits === 1 ? '' : 's'}. Going over is fine when there is a reason — it is going over silently that is not.`,
    },
    { label: 'Past their SLA', value: p.breached, hint: 'Finished later than promised' },
    { label: 'Repeat visits', value: p.wasted, hint: 'Came back for the same fault' },
    { label: 'Missing photos', value: p.missingEvidence, hint: 'Closed without the evidence' },
    { label: 'Jobs declined', value: p.declined, hint: 'Passed to somebody else' },
  ].filter((row) => Number(row.value) > 0);

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-heading text-lg">
          <TrendingUp className="h-5 w-5 text-primary-600" aria-hidden="true" />
          Your performance
        </h3>

        <div className="flex gap-1 rounded-xl bg-muted p-1">
          {RANGES.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setDays(value)}
              aria-pressed={days === value}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                days === value
                  ? 'bg-card text-primary-700 shadow-card'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        {good.map((row) => (
          <div key={row.label} className="card-soft">
            <p className="stat-figure text-primary-700">{row.value}</p>
            <p className="mt-0.5 text-sm font-medium">{row.label}</p>
            <p className="text-xs text-muted-foreground">{row.hint}</p>
          </div>
        ))}
      </div>

      {/* What customers said. Shown separately because it is the only figure
          here that comes from outside the company. */}
      {p.rating !== null && (
        <div className="mt-3 flex items-center gap-3 rounded-2xl bg-primary-50 p-4">
          <Star className="h-5 w-5 shrink-0 text-primary-600" aria-hidden="true" />
          <div>
            <p className="font-semibold tabular-nums">
              {p.rating.toFixed(1)} out of 5
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                from {p.ratingCount} {p.ratingCount === 1 ? 'customer' : 'customers'}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              What the people you visited said afterwards.
            </p>
          </div>
        </div>
      )}

      {attention.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold">Worth a look</h4>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {attention.map((row) => (
              <li
                key={row.label}
                className="flex items-baseline justify-between gap-3 rounded-xl border border-border/70 px-3 py-2"
              >
                <span>
                  <span className="text-sm font-medium">{row.label}</span>
                  <span className="block text-xs text-muted-foreground">{row.hint}</span>
                </span>
                <span className="font-heading text-lg font-bold tabular-nums">{row.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-4 text-xs text-muted-foreground">
        These are the same figures your admin sees — nothing here is hidden from you, and there is
        no single overall score. Counts cover the last {days} days.
      </p>
    </div>
  );
}
